<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import QRCode from 'qrcode';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  inbox: { type: Object, default: null },
});

const emit = defineEmits(['update:show', 'paid', 'close']);

const { t } = useI18n();
const dialogRef = ref(null);
const step = ref(1);
const selectedNetwork = ref('tron');
const order = ref(null);
const qrDataUrl = ref('');
const loading = ref(false);
const polling = ref(false);
const confirming = ref(false);
const secondsLeft = ref(0);
let pollTimer = null;
let countdownTimer = null;

const networks = [
  { id: 'tron', label: 'USDT · TRON Mainnet', network: 'TRON' },
  { id: 'eth', label: 'USDT · Ethereum Mainnet', network: 'Ethereum' },
];

const clearTimers = () => {
  if (pollTimer) clearInterval(pollTimer);
  if (countdownTimer) clearInterval(countdownTimer);
  pollTimer = null;
  countdownTimer = null;
};

const resetState = () => {
  step.value = 1;
  selectedNetwork.value = 'tron';
  order.value = null;
  qrDataUrl.value = '';
  loading.value = false;
  polling.value = false;
  secondsLeft.value = 0;
  clearTimers();
};

const close = () => {
  dialogRef.value?.close();
  emit('update:show', false);
  emit('close');
  resetState();
};

const formatCountdown = () => {
  const s = Math.max(0, secondsLeft.value);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};

const startCountdown = () => {
  clearTimers();
  countdownTimer = setInterval(() => {
    secondsLeft.value = Math.max(0, secondsLeft.value - 1);
    if (secondsLeft.value <= 0) {
      clearTimers();
      useAlert(t('INBOX_MGMT.SUBSCRIPTION.ORDER_EXPIRED'));
    }
  }, 1000);
};

const createOrder = async () => {
  if (!props.inbox?.id) return;
  loading.value = true;
  try {
    const { data } = await InboxesAPI.createSubscriptionOrder(
      props.inbox.id,
      selectedNetwork.value
    );
    order.value = data;
    secondsLeft.value = data.seconds_remaining || 3600;
    qrDataUrl.value = await QRCode.toDataURL(data.pay_address, {
      width: 220,
      margin: 1,
    });
    step.value = 2;
    startCountdown();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SUBSCRIPTION.CREATE_ORDER_ERROR')
    );
  } finally {
    loading.value = false;
  }
};

const startPolling = () => {
  if (!order.value || polling.value) return;
  polling.value = true;
  pollTimer = setInterval(async () => {
    try {
      const { data } = await InboxesAPI.getSubscriptionOrder(
        props.inbox.id,
        order.value.id
      );
      order.value = data;
      if (data.status === 'paid') {
        clearTimers();
        useAlert(t('INBOX_MGMT.SUBSCRIPTION.PAID_SUCCESS'));
        emit('paid', data);
        close();
      } else if (data.status === 'expired') {
        clearTimers();
        useAlert(t('INBOX_MGMT.SUBSCRIPTION.ORDER_EXPIRED'));
      }
    } catch {
      // ignore transient poll errors
    }
  }, 5000);
};

const copyAddress = async () => {
  try {
    await navigator.clipboard.writeText(order.value.pay_address);
    useAlert(t('INBOX_MGMT.SUBSCRIPTION.COPIED'));
  } catch {
    useAlert(t('INBOX_MGMT.SUBSCRIPTION.COPY_FAILED'));
  }
};

const confirmManually = async () => {
  if (!order.value || confirming.value) return;
  if (!window.confirm(t('INBOX_MGMT.SUBSCRIPTION.MANUAL_CONFIRM_HINT'))) {
    return;
  }
  confirming.value = true;
  try {
    const { data } = await InboxesAPI.confirmSubscriptionOrder(
      props.inbox.id,
      order.value.id
    );
    order.value = data;
    clearTimers();
    useAlert(t('INBOX_MGMT.SUBSCRIPTION.PAID_SUCCESS'));
    emit('paid', data);
    close();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.SUBSCRIPTION.MANUAL_CONFIRM_ERROR')
    );
  } finally {
    confirming.value = false;
  }
};

const openDialog = async () => {
  await nextTick();
  // Dialog uses native <dialog>; open after mount so ref exists
  dialogRef.value?.open();
};

watch(
  () => props.show,
  async value => {
    if (value) {
      await openDialog();
    } else {
      dialogRef.value?.close();
      resetState();
    }
  }
);

onMounted(() => {
  if (props.show) openDialog();
});

onBeforeUnmount(clearTimers);
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('INBOX_MGMT.SUBSCRIPTION.TITLE')"
    width="lg"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="close"
  >
    <div v-if="inbox" class="flex flex-col gap-4">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('INBOX_MGMT.SUBSCRIPTION.DESCRIPTION', { inbox: inbox.name }) }}
      </p>

      <template v-if="step === 1">
        <div class="text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.SUBSCRIPTION.SELECT_NETWORK') }}
        </div>
        <div class="grid gap-3">
          <button
            v-for="item in networks"
            :key="item.id"
            type="button"
            class="flex items-center justify-between rounded-xl border px-4 py-3 text-left transition"
            :class="
              selectedNetwork === item.id
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:border-n-strong'
            "
            @click="selectedNetwork = item.id"
          >
            <div>
              <div class="font-medium text-n-slate-12">{{ item.label }}</div>
              <div class="text-xs text-n-slate-11">{{ item.network }}</div>
            </div>
          </button>
        </div>
        <div class="rounded-xl bg-n-alpha-2 px-4 py-3">
          <div class="text-xs text-n-slate-11">
            {{ t('INBOX_MGMT.SUBSCRIPTION.AMOUNT_LABEL') }}
          </div>
          <div class="text-2xl font-semibold text-n-slate-12">
            ~20.xxx USDT / 30 {{ t('INBOX_MGMT.SUBSCRIPTION.DAYS') }}
          </div>
        </div>
        <Button
          :label="t('INBOX_MGMT.SUBSCRIPTION.OPEN_CHECKOUT')"
          class="w-full"
          :is-loading="loading"
          @click="createOrder"
        />
      </template>

      <template v-else-if="order">
        <div class="text-center">
          <div class="text-sm text-n-slate-11">
            {{ t('INBOX_MGMT.SUBSCRIPTION.PAY_AMOUNT') }}
          </div>
          <div class="text-3xl font-bold text-n-slate-12">
            {{ order.amount }}
            <span class="text-base font-medium">USDT</span>
          </div>
          <div class="mt-1 text-xs uppercase text-n-slate-11">
            {{ order.network === 'tron' ? 'TRON (TRC20)' : 'ETH (ERC20)' }}
          </div>
        </div>

        <div
          class="mx-auto overflow-hidden rounded-xl border border-n-weak p-3"
        >
          <img
            v-if="qrDataUrl"
            :src="qrDataUrl"
            alt="payment-qr"
            class="size-52"
          />
        </div>

        <div class="rounded-xl bg-n-alpha-2 px-3 py-2">
          <div class="text-xs text-n-slate-11">
            {{ t('INBOX_MGMT.SUBSCRIPTION.ADDRESS') }}
          </div>
          <div class="mt-1 break-all font-mono text-xs text-n-slate-12">
            {{ order.pay_address }}
          </div>
          <Button
            :label="t('INBOX_MGMT.SUBSCRIPTION.COPY_ADDRESS')"
            sm
            slate
            class="mt-2"
            @click="copyAddress"
          />
        </div>

        <div class="text-center text-sm text-n-slate-11">
          {{
            t('INBOX_MGMT.SUBSCRIPTION.COUNTDOWN', {
              time: formatCountdown(),
            })
          }}
        </div>

        <div class="rounded-xl bg-n-amber-2 px-3 py-2 text-xs text-n-amber-11">
          {{ t('INBOX_MGMT.SUBSCRIPTION.WARNING') }}
        </div>

        <Button
          :label="t('INBOX_MGMT.SUBSCRIPTION.I_PAID')"
          class="w-full"
          :is-loading="polling"
          @click="startPolling"
        />
        <Button
          :label="t('INBOX_MGMT.SUBSCRIPTION.MANUAL_CONFIRM')"
          class="w-full"
          slate
          :is-loading="confirming"
          @click="confirmManually"
        />
        <p class="mb-0 text-center text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.SUBSCRIPTION.MANUAL_CONFIRM_NOTE') }}
        </p>
        <button
          type="button"
          class="text-center text-sm text-n-brand"
          @click="close"
        >
          {{ t('INBOX_MGMT.SUBSCRIPTION.CANCEL') }}
        </button>
      </template>
    </div>
  </Dialog>
</template>
